import { type MoveEvent, TYPE_MASK, WALL, nextStorm, ringOf } from '@chessbut/engine';
import type { Decoration, VariantUi } from './ui.ts';

const STORM = '/art/storm.svg';
const RUBBLE = '/art/rubble.svg';
const stormHit = (e: MoveEvent) => e.t === 'add' && e.cause === 'storm';

export const stormUi: VariantUi = {
  heroImage: STORM,
  peek: [{ image: RUBBLE }, { image: RUBBLE }, { image: RUBBLE }, { image: RUBBLE }, { image: '/art/wK.svg', threat: true }, {}, { image: RUBBLE }, {}, { image: '/art/bN.svg' }],

  pieceImage(_pos, _sq, piece) {
    return (piece & TYPE_MASK) === WALL ? RUBBLE : null;
  },

  pieceLabel(_pos, _sq, piece) {
    return (piece & TYPE_MASK) === WALL ? 'collapsed square' : null;
  },

  decorations(pos) {
    // The ring that goes next: outlined as it gets close, shaded for the final round.
    const next = nextStorm(pos);
    if (!next || next.in > 8) return [];
    const out: Decoration[] = [];
    for (let sq = 0; sq < 64; sq++) {
      if (ringOf(sq) === next.ring) out.push({ kind: 'mark', sq, tone: next.in <= 2 ? 'doomed' : 'info' });
    }
    return out;
  },

  twist(pos, you) {
    const next = nextStorm(pos);
    if (!next) return { text: 'The storm is over: a 4×4 arena, last king standing.' };
    const where = next.ring === 0 ? 'The edge' : 'The next ring';
    const k = pos.royals[pos.turn];
    const yours = you === null || you === pos.turn;
    if (next.in <= 2 && k >= 0 && ringOf(k) <= next.ring) {
      return {
        text: yours ? 'Get your king off the edge before it collapses!' : 'Their king has to get off the edge!',
        alert: yours,
      };
    }
    // Counted in moves per player, which is what you feel at the board.
    const moves = Math.ceil(next.in / 2);
    return {
      text: next.in === 1 ? `${where} collapses after this move!` : `${where} collapses in ${moves} ${moves === 1 ? 'move' : 'moves'}`,
      pips: { on: 10 - moves, total: 10, title: 'Moves until the board shrinks' },
      alert: next.in <= 2,
    };
  },

  threatText: (you) => (you ? 'The ground under your king is about to collapse. Move it!' : 'A king is on ground about to collapse!'),

  eventSound(e) {
    return stormHit(e) ? 'rumble' : null;
  },

  moveBadge(rec) {
    if (!rec.events.some(stormHit)) return null;
    const lost = rec.events.filter((e) => e.t === 'remove' && e.cause === 'storm').length;
    return { icon: STORM, title: lost ? `The board shrank: ${lost} destroyed` : 'The board shrank' };
  },
};
